/* eslint-disable max-len */
import React from 'react'
import { Button, Col, Dropdown, notification, Row, Typography } from 'antd'
import { AddToCalendarMenu } from './add-to-calendar-menu'
import 'moment/locale/fr'

const { Text } = Typography

import { getLeavePeriod} from '@vacationtracker/shared/functions/get-leave-period'
import { displayLeaveInDaysAndHours } from '@vacationtracker/shared/components/display-leave-in-days-and-hours'
import { LeaveDisplayFormatEnum } from '@vacationtracker/shared/types/display-in-days-hours'

import { IGenerateNotificationValues, IUpdateLeaveRequestNotification } from './types'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'


const getRequestor = (
  userId,
  authUser,
  subscriptionEvent,
  userName
) => {
  if (userId === authUser?.id) {
    return ''
  }

  if (subscriptionEvent?.eventType === 'LEAVE_REQUEST_ADDED') {
    return userName
  }

  return `${userName}'s`
}


const generateNotification = (values: IGenerateNotificationValues, leaveRequest: any, autoApproved: boolean, formatMessage: any, hourlyLeaveAccounting: boolean) => {
  const showRequestedRemainingInfo = values.showRequestedRemainingInfo
  const showAddToCalendarButton = values.showAddToCalendar
  const sameArgsForDisplay = {
    format: LeaveDisplayFormatEnum.long,
    formatMessage,
  }
  const displayRequestedCurrent = displayLeaveInDaysAndHours({
    value: values.requestedDays,
    ...sameArgsForDisplay,
  })
  const displayRemainingCurrent = displayLeaveInDaysAndHours({
    value: values.currentDays,
    ...sameArgsForDisplay,
  })

  const displayRequestedNext = displayLeaveInDaysAndHours({
    value:values.requestedNextDays,
    ...sameArgsForDisplay,
  })
  const displayRemainingNext = displayLeaveInDaysAndHours({
    value: values.nextDays,
    ...sameArgsForDisplay,
  })

  const apiUrl = process.env.REACT_APP_API_URL || ''
  const requestData = { ...leaveRequest, leaveTypeName: values.leaveTypeName }

  return {
    key: values.key,
    description: (
      <div>
        <div>
          <Row>
            <Col span={12}>{formatMessage({ id: 'components.leaveRequestAction.leaveTypeLabel' })}</Col>
            <Col span={12}>{formatMessage({ id: 'components.leaveRequestAction.periodLabel' })}</Col>
          </Row>
          <Row>
            <Col span={12}><Text strong>{values.leaveTypeName}</Text></Col>
            <Col span={12}><Text strong>{values.leavePeriod}</Text></Col>
          </Row>
        </div>
        {showRequestedRemainingInfo && values.requestedDays > 0 &&
          <div>
            <Row>
              <Col span={12}>{formatMessage({ id: 'leaveRequests.requestedCurrentYear'})}</Col>
              <Col span={12}>{formatMessage({ id: 'leaveRequests.remainingCurrentYear' })}</Col>
            </Row>
            <Row>
              <Col span={12}>
                <Text strong>{displayRequestedCurrent}</Text>
              </Col>
              <Col span={12}>
                <Text strong>{
                  values.hasUnlimitedDays ? formatMessage({ id: 'app.unlimited' }) : displayRemainingCurrent
                }</Text>
              </Col>
            </Row>
          </div>
        }
        {showRequestedRemainingInfo && values.requestedNextDays > 0 &&
          <div>
            <Row>
              <Col span={12}>{formatMessage({ id: 'leaveRequests.requestedNextYear'})}</Col>
              <Col span={12}>{formatMessage({ id: 'leaveRequests.remainingNextYear'})}</Col>
            </Row>
            <Row>
              <Col span={12}>
                <Text strong>{displayRequestedNext}</Text>
              </Col>
              <Col span={12}>
                <Text strong>{
                  values.hasUnlimitedDaysNextYear ? formatMessage({ id: 'app.unlimited' }) : displayRemainingNext
                }</Text>
              </Col>
            </Row>
          </div>
        }
        {!autoApproved &&
          <div>
            <Row>
              <Col span={12}>{formatMessage({ id: 'app.role.approvers'})}:</Col>
            </Row>
            <Row>
              <Col span={24}><Text strong>{values.approvers.map((a) => a.name).join(', ')}</Text></Col>
            </Row>
          </div>
        }
        {showAddToCalendarButton &&
          <div style={{ marginTop: 20 }}>
            <Dropdown
              overlay={<AddToCalendarMenu apiUrl={apiUrl} requestData={requestData} formatMessage={formatMessage} />}
              placement="bottomLeft"
            >
              <Button>{ formatMessage({ id: 'components.leaveRequestAction.addToCalendar' }) }</Button>
            </Dropdown>
          </div>
        }
      </div>
    ),
    message: values.message,
    duration: values.duration,
  }
}

export const updateLeaveRequestNotification = ({
  subscriptionEvent,
  leaveRequest,
  result,
  authUser,
  authCompany,
  formatMessage,
  locale,
  isCalendarConnected,
}: IUpdateLeaveRequestNotification): { key: string } | void | object => {
  const {
    isPartDay,
    startDate,
    endDate,
    partDayStartHour,
    partDayEndHour,
    autoApproved,
    user: { team, leaveDays, name: userName, id: userId },
    leaveType,
    workingDays,
    totalThisYear,
    totalNextYear,
  } = leaveRequest.getLeaveRequest

  const { name: editorName, id: editorId } = leaveRequest.getUser
  const leaveTypeId = leaveType.id
  let currentYearDays
  let nextYearDays
  const currentYearIndex = leaveDays.findIndex(year => {
    if (totalThisYear > 0) {
      return new Date().toISOString() > year.yearStart && new Date().toISOString() < year.yearEnd
    }
  })
  const nextYearIndex = leaveDays.findIndex(year => {
    if (totalNextYear > 0) {
      return new Date(startDate).toISOString() < year.yearStart && new Date(endDate).toISOString() < year.yearEnd
    }
  })
  if (currentYearIndex !== -1) {
    currentYearDays = currentYearIndex !== -1 && leaveDays[currentYearIndex].leaveTypes.find(lt => lt.leaveTypeId === leaveTypeId)
  }

  if (nextYearIndex !== -1) {
    nextYearDays = nextYearIndex !== -1 && leaveDays[nextYearIndex].leaveTypes.find(lt => lt.leaveTypeId === leaveTypeId)
  }
  const currentDays = currentYearDays?.currentDays ?? 0
  const nextDays = nextYearDays?.currentDays ?? 0

  const hasUnlimitedDays =  Boolean(currentYearDays?.hasUnlimitedDays)
  const hasUnlimitedDaysNextYear =  Boolean(nextYearDays?.hasUnlimitedDays)
  let showRequestedRemainingInfo = true
  let showAddToCalendar = false

  let title
  const actionCreator = editorId === authUser?.id ? '' : editorName
  const requestor = getRequestor(userId, authUser, subscriptionEvent, userName)
  const titleValues = { actionCreator, requestor }
  const descriptionValues = {
    editor: editorName,
    action: '',
    requestedDays: result.totalThisYear,
    requestedNextDays: result.totalNextYear,
    currentDays,
    nextDays,
    leaveTypeName: leaveType.name,
    leavePeriod: getLeavePeriod({
      isPartDay,
      startDate,
      endDate,
      partDayStartHour,
      partDayEndHour,
      formatMessage,
      leaveRequestWorkingDays: workingDays,
      locale,
      hourFormat: authUser?.hourFormat || HourFormatEnum.twentyFour,
    }),
    approvers: team.approvers,
  }

  const message: string = subscriptionEvent.message
  switch (subscriptionEvent?.eventType) {
    case 'LEAVE_REQUEST_APPROVED':
      if (autoApproved) {
        if (result?.isEdited && result?.originalVersion.status === 'APPROVED') {
          title = 'components.leaveRequestAction.editedAutoApproved'
        } else {
          title = 'components.leaveRequestAction.autoApproved'
        }
      } else {
        title = 'components.leaveRequestAction.approved'
      }
      showRequestedRemainingInfo = false
      showAddToCalendar = !isCalendarConnected && authUser.id === result.userId
      break

    case 'LEAVE_REQUEST_DENIED':
      if (result && result?.isEdited && result?.originalVersion.status === 'APPROVED') {
        title = 'components.leaveRequestAction.editedDenied'
      } else {
        title = 'components.leaveRequestAction.denied'
      }
      showRequestedRemainingInfo = false
      break

    case 'LEAVE_REQUEST_CANCELLED':
      title = 'components.leaveRequestAction.cancelled'
      break

    case 'LEAVE_REQUEST_ADDED':
      title = 'components.leaveRequestAction.added'
      break
    case 'LEAVE_REQUEST_UPDATED':
      if (result?.isEdited && result?.status === 'APPROVED') {
        title = 'components.leaveRequestAction.editedApproved'
      } else {
        title = 'components.leaveRequestAction.edited'
      }
      break

    case 'LEAVE_REQUEST_CREATED':
      title = 'components.leaveRequestAction.created'
      break

    case 'LEAVE_REQUEST_DELETED':
      title = 'components.leaveRequestAction.deleted'
      break
  }

  const getMessageValue = (title, titleValues, message) => {
    const formatedMessage = formatMessage(
      { id: title },
      {
        actionCreator: titleValues.actionCreator,
        byActionCreator: (...chunks) => titleValues?.actionCreator ? chunks : '',
        requestor: titleValues.requestor,
        forUser: (...chunks) => titleValues?.requestor ? chunks : '',
      }
    )
    if (title && formatedMessage) {
      return formatedMessage
    } else if (/^error\.[a-zA-Z0-9]+$/.test(message)) {
      return formatMessage({ id: message })
    } else {
      return message
    }
  }

  const generatedNotification = generateNotification(
    {
      key: subscriptionEvent.correlationId,
      message: getMessageValue(title, titleValues, message),
      showRequestedRemainingInfo,
      showAddToCalendar,
      hasUnlimitedDays,
      hasUnlimitedDaysNextYear,
      ...descriptionValues,
      duration: 30,
    },
    leaveRequest.getLeaveRequest,
    autoApproved,
    formatMessage,
    Boolean(authCompany.hourlyLeaveAccounting)
  )

  // notify other user approvers
  if (authUser?.approverTo?.find(u => u.id === subscriptionEvent.userId) && authUser.id !== subscriptionEvent.creator) {
    notification.open(generatedNotification)
  }

  // notify approver of a user who changed leave request
  if (authUser?.approverTo?.find(u => u.id === subscriptionEvent.userId) && editorId === subscriptionEvent.creator) {
    notification.open(generatedNotification)
  }

  // notify user leave request belongs to
  if (authUser?.id === subscriptionEvent.userId) {
    notification.open(generatedNotification)
  }

  // notify user leave request belongs to when AUTO APPROVED
  if (authUser?.id === subscriptionEvent.userId && editorId === authUser.id && autoApproved && subscriptionEvent.eventType !== 'LEAVE_REQUEST_UPDATED') {
    notification.open(generatedNotification)
  }
}